import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Smartphone, Wifi, Tv, Zap, Droplet, Flame, Building2, Radio, MoreHorizontal, Search, Archive, Upload, Download, Wand2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import TxnTypeBadge from "@/components/TxnTypeBadge";

const KIND_ICON = {
    "Mobile Postpaid":     Smartphone,
    "Mobile Prepaid":      Smartphone,
    "Broadband":           Wifi,
    "Internet Lease Line": Wifi,
    "Landline":            Radio,
    "DTH":                 Tv,
    "OTT":                 Tv,
    "Electricity":         Zap,
    "Water":               Droplet,
    "Gas Cylinder":        Flame,
    "Piped Gas":           Flame,
    "Society Maintenance": Building2,
    "Other Utility":       MoreHorizontal,
};

const STATUS_STYLE = {
    Active:       "text-emerald-700 border-emerald-200 bg-emerald-50",
    Inactive:     "text-stone-500 border-stone-200 bg-stone-50",
    Suspended:    "text-amber-700 border-amber-200 bg-amber-50",
    Ported:       "text-blue-700 border-blue-200 bg-blue-50",
    Closed:       "text-stone-500 border-stone-200 bg-stone-50",
    Disconnected: "text-red-700 border-red-200 bg-red-50",
};

export default function Bills() {
    const { hasRole } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [meta, setMeta] = useState({ kinds: [], statuses: [] });
    const [q, setQ] = useState("");
    const [kindFilter, setKindFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("Active");
    const [busy, setBusy] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [detectOpen, setDetectOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [detectLoading, setDetectLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());

    const load = () => {
        api.get("/bills").then((r) => setRows(r.data || []));
        api.get("/bills/summary").then((r) => setSummary(r.data));
        api.get("/bills/meta").then((r) => setMeta(r.data));
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => rows.filter((r) => {
        if (kindFilter !== "all" && r.kind !== kindFilter) return false;
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        if (q) {
            const needle = q.toLowerCase();
            const hay = [r.operator, r.account_or_number, r.account_label, r.holder_name,
                         r.plan_name, r.family_member, r.masked_account].join(" ").toLowerCase();
            if (!hay.includes(needle)) return false;
        }
        return true;
    }), [rows, q, kindFilter, statusFilter]);

    const archive = async (id) => {
        if (!window.confirm("Archive this connection? It will disappear from active views.")) return;
        setBusy(true);
        try {
            await api.delete(`/bills/${id}`);
            toast.success("Archived");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to archive");
        } finally { setBusy(false); }
    };

    const downloadTemplate = async () => {
        try {
            const r = await api.get("/bills/import/template");
            const csvString = r.data?.csv || "";
            const blob = new Blob([csvString], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "nexus-bills-template.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Template downloaded");
        } catch (e) {
            toast.error("Failed to fetch template");
        }
    };

    const doImport = async () => {
        if (!importFile) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            const r = await api.post("/bills/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImportResult(r.data);
            if (r.data.created > 0) toast.success(`Imported ${r.data.created} connection(s)`);
            if (r.data.errors > 0) toast.error(`${r.data.errors} row(s) had errors`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally { setImporting(false); }
    };

    const openDetect = async () => {
        setDetectOpen(true);
        setDetectLoading(true);
        setSelected(new Set());
        try {
            const r = await api.get("/bills/reconcile/candidates", { params: { days: 90 } });
            setCandidates(r.data.candidates || []);
        } catch (e) {
            toast.error("Failed to detect candidates");
        } finally { setDetectLoading(false); }
    };

    const toggleCand = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const acceptDetected = async () => {
        const matches = candidates
            .filter((c) => selected.has(c.expense_id || c.statement_txn_key))
            .map((c) => c);  // pass whole object — backend uses source field
        if (matches.length === 0) { toast.error("Select at least one candidate"); return; }
        try {
            const r = await api.post("/bills/reconcile/accept", { matches });
            toast.success(`Linked ${r.data.created} payment(s) to bills`);
            setDetectOpen(false);
            setCandidates([]);
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to accept matches");
        }
    };

    return (
        <div data-testid="bills-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow">Recurring Bills · Mobile · Broadband · Utilities</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Every connection, one place.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Track mobile numbers, broadband, DTH and utility connections just like your credit cards —
                        with invoices, payments, and family attribution.
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={async () => {
                            const doAuto = window.confirm("Auto-post confirmed extractions? OK = auto-post · Cancel = queue for review.");
                            const t = toast.loading("Scanning Gmail for bill emails…");
                            try {
                                const r = await api.post("/email-capture/sync-bills", null, { params: { days: 45, auto_post: doAuto } });
                                toast.dismiss(t);
                                const d = r.data;
                                toast.success(`Scanned ${d.processed} · matched ${d.matched} · created ${d.created} · reviews ${d.reviews}`);
                                load();
                            } catch (e) {
                                toast.dismiss(t);
                                toast.error(e?.response?.data?.detail || "Gmail sync failed");
                            }
                        }} data-testid="bills-gmail-sync">
                            <Wifi size={14} className="mr-1.5" /> Sync Gmail
                        </Button>
                        <Button variant="outline" onClick={openDetect} data-testid="bills-detect-btn">
                            <Wand2 size={14} className="mr-1.5" /> Detect from cards
                        </Button>
                        <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="bills-import-btn">
                            <Upload size={14} className="mr-1.5" /> Import CSV
                        </Button>
                        <Button onClick={() => navigate("/bills/new")} data-testid="bills-add-btn" className="bg-stone-900 hover:bg-stone-800 text-white">
                            <Plus size={16} className="mr-1.5" /> Add connection
                        </Button>
                    </div>
                )}
            </div>

            {/* KPIs */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="kpi-card">
                        <div className="eyebrow">Active connections</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{summary.active_bills}<span className="text-sm text-stone-400 ml-1">/ {summary.total_bills}</span></div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow">Monthly outflow · est</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(summary.monthly_outflow_estimate)}</div>
                        <div className="text-[11px] text-stone-500 mt-1">B: {fmtMoney(summary.by_business?.Business)} · P: {fmtMoney(summary.by_business?.Personal)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow">Dues this month</div>
                        <div className={`font-display text-2xl mt-2 tabular-nums ${summary.dues_this_month > 0 ? "text-amber-700" : ""}`}>{fmtMoney(summary.dues_this_month)}</div>
                        <div className="text-[11px] text-stone-500 mt-1">{summary.unpaid_invoices_this_month} unpaid invoice(s)</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow">Missing invoices · prev month</div>
                        <div className={`font-display text-2xl mt-2 tabular-nums ${summary.missing_invoices_prev_month > 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {summary.missing_invoices_prev_month}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[180px] max-w-sm">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search operator, number, holder…"
                        className="pl-8 h-9"
                        data-testid="bills-search"
                    />
                </div>
                <Select value={kindFilter} onValueChange={setKindFilter}>
                    <SelectTrigger className="w-[180px] h-9" data-testid="bills-kind-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All kinds</SelectItem>
                        {meta.kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px] h-9" data-testid="bills-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {(meta.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <div className="text-xs text-stone-500 ml-2">{filtered.length} shown</div>
            </div>

            {/* Table */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Kind</th>
                            <th>Operator</th>
                            <th>Number / Account</th>
                            <th>Holder</th>
                            <th>Plan</th>
                            <th className="text-right">Monthly ₹</th>
                            <th>Due day</th>
                            <th>B/P</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={10} className="text-center text-stone-500 py-14">
                                No connections match this filter. {hasRole("admin", "finance") && <Link to="/bills/new" className="text-stone-900 underline">Add one</Link>}.
                            </td></tr>
                        )}
                        {filtered.map((r) => {
                            const Icon = KIND_ICON[r.kind] || MoreHorizontal;
                            const st = STATUS_STYLE[r.status] || STATUS_STYLE.Inactive;
                            return (
                                <tr key={r.id} data-testid={`bill-row-${r.id}`} className="cursor-pointer" onClick={() => navigate(`/bills/${r.id}`)}>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-6 h-6 rounded-md flex items-center justify-center text-white"
                                                style={{ backgroundColor: r.color_tag || "#0369a1" }}
                                            >
                                                <Icon size={12} />
                                            </span>
                                            <span className="text-xs text-stone-600">{r.kind}</span>
                                        </div>
                                    </td>
                                    <td className="font-medium text-stone-900">{r.operator}</td>
                                    <td className="font-mono-figure tabular-nums text-sm">{r.masked_account || r.account_or_number}
                                        {r.account_label && <div className="text-[11px] text-stone-500 font-sans">{r.account_label}</div>}
                                    </td>
                                    <td>{r.holder_name || "—"}
                                        {r.family_member && <div className="text-[11px] text-stone-500">{r.family_member}</div>}
                                    </td>
                                    <td className="text-xs text-stone-600">{r.plan_name || "—"}</td>
                                    <td className="text-right tabular-nums font-medium">{fmtMoney(r.monthly_rent)}</td>
                                    <td className="tabular-nums text-xs">{r.payment_due_day || "—"}</td>
                                    <td>
                                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${r.business_personal === "Business" ? "text-blue-700 border-blue-200 bg-blue-50" : "text-stone-600 border-stone-200 bg-stone-50"}`}>
                                            {r.business_personal?.[0] || "P"}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${st}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            {hasRole("admin", "finance") && (
                                                <>
                                                    <button className="text-xs px-2 py-1 rounded hover:bg-stone-100" onClick={() => navigate(`/bills/${r.id}/edit`)}>Edit</button>
                                                    <button className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" onClick={() => archive(r.id)} disabled={busy} title="Archive">
                                                        <Archive size={12} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </div>

            {/* CSV Import dialog */}
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Bulk-import connections from CSV</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm text-stone-600">
                            Upload a CSV of your existing mobile numbers, broadband, and utility connections.
                            Duplicates (same operator + account number) are skipped automatically.
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={downloadTemplate} data-testid="bills-download-template">
                                <Download size={13} className="mr-1.5" /> Download template
                            </Button>
                            <div className="text-xs text-stone-500">One header row + one sample row</div>
                        </div>
                        <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                                className="text-sm"
                                data-testid="bills-import-file"
                            />
                            {importFile && (
                                <div className="text-xs text-stone-500 mt-2">Selected: {importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>
                            )}
                        </div>
                        {importResult && (
                            <div className="rounded-lg border border-stone-200 p-3 bg-stone-50" data-testid="bills-import-result">
                                <div className="flex items-center gap-4 text-sm mb-2">
                                    <span className="text-emerald-700 font-medium">✓ {importResult.created} created</span>
                                    <span className="text-amber-700">⤵ {importResult.skipped} skipped</span>
                                    <span className="text-red-700">✗ {importResult.errors} errors</span>
                                </div>
                                {importResult.errors_items?.length > 0 && (
                                    <details className="text-xs">
                                        <summary className="cursor-pointer text-red-700">Show error rows</summary>
                                        <ul className="mt-2 space-y-1 text-stone-600">
                                            {importResult.errors_items.map((e, i) => (
                                                <li key={i} className="tabular-nums">Row {e.row}: {e.reason}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                                {importResult.unknown_columns?.length > 0 && (
                                    <div className="text-[11px] text-stone-500 mt-2">
                                        Unknown columns ignored: {importResult.unknown_columns.join(", ")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
                        <Button
                            onClick={doImport}
                            disabled={!importFile || importing}
                            className="bg-stone-900 hover:bg-stone-800 text-white"
                            data-testid="bills-import-run"
                        >
                            {importing ? "Importing…" : "Import"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Auto-detect bill payments dialog */}
            <Dialog open={detectOpen} onOpenChange={setDetectOpen}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wand2 size={16} /> Detect bill payments in card expenses
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-stone-600 mb-3">
                        We scan the last 90 days of card expenses and flag anything that looks like a
                        bill payment (Airtel, Jio, BSES, Tata Power, etc). Tick the ones to reconcile
                        into <span className="text-stone-900 font-medium">Bill Payments</span> —
                        already-linked expenses are skipped automatically.
                    </div>
                    {detectLoading ? (
                        <div className="py-10 text-center text-stone-500">Scanning…</div>
                    ) : candidates.length === 0 ? (
                        <div className="py-10 text-center text-stone-500">
                            No unlinked bill-payment candidates found. Everything is already reconciled.
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 text-xs mb-2">
                                <button
                                    className="text-stone-500 hover:text-stone-900 underline"
                                    onClick={() => setSelected(new Set(candidates.map((c) => c.expense_id || c.statement_txn_key)))}
                                >Select all {candidates.length}</button>
                                <button
                                    className="text-stone-500 hover:text-stone-900 underline"
                                    onClick={() => setSelected(new Set())}
                                >Clear</button>
                                <span className="ml-auto text-stone-500">{selected.size} selected</span>
                            </div>
                            <div className="border border-stone-200 rounded-lg overflow-hidden">
                                <table className="dense-table w-full">
                                    <thead>
                                        <tr>
                                            <th className="w-8"></th>
                                            <th>Date</th>
                                            <th>Merchant</th>
                                            <th>Type</th>
                                            <th className="text-right">Amount</th>
                                            <th>→</th>
                                            <th>Matched bill</th>
                                            <th className="text-center">Match</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {candidates.map((c) => {
                                            const cid = c.expense_id || c.statement_txn_key;
                                            const isCredit = (c.transaction_type || "Debit") === "Credit";
                                            return (
                                            <tr key={cid} data-testid={`detect-row-${cid}`}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(cid)}
                                                        onChange={() => toggleCand(cid)}
                                                        data-testid={`detect-check-${cid}`}
                                                    />
                                                </td>
                                                <td className="tabular-nums text-xs">{c.expense_date}</td>
                                                <td className="text-sm">
                                                    {c.merchant}
                                                    <div className="text-[10px] uppercase flex items-center gap-1">
                                                        <span className={c.source === "statement_txn" ? "text-blue-700" : "text-stone-400"}>
                                                            {c.source === "statement_txn" ? "STATEMENT" : "EXPENSE"}
                                                        </span>
                                                        {c.category && <span className="text-stone-400">· {c.category}</span>}
                                                    </div>
                                                </td>
                                                <td><TxnTypeBadge value={c.transaction_type} /></td>
                                                <td className={`text-right tabular-nums font-medium ${isCredit ? "text-emerald-700" : ""}`}>{isCredit ? "+ " : ""}₹{c.amount.toLocaleString()}</td>
                                                <td className="text-stone-400">→</td>
                                                <td>
                                                    <div className="text-stone-900">{c.bill_operator}</div>
                                                    <div className="text-[11px] text-stone-500">{c.bill_kind} · {c.bill_masked_account}</div>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${c.score >= 90 ? "text-emerald-700 border-emerald-200 bg-emerald-50" : c.score >= 75 ? "text-blue-700 border-blue-200 bg-blue-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>
                                                        {c.score}%
                                                    </span>
                                                    <div className="text-[10px] text-stone-400 mt-0.5">{c.reason}</div>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetectOpen(false)}>Close</Button>
                        <Button
                            onClick={acceptDetected}
                            disabled={selected.size === 0}
                            className="bg-emerald-700 hover:bg-emerald-800 text-white"
                            data-testid="detect-accept-btn"
                        >
                            <CheckCircle2 size={13} className="mr-1.5" /> Reconcile {selected.size || ""}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
